import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Wallet, Users, Package, ShoppingCart, TrendingUp, AlertTriangle } from "lucide-react";
import MorningBrief from "./MorningBrief";

export default function Dashboard() {
  const { tenant } = useAuth();
  const [k, setK] = useState(null);
  const [err, setErr] = useState("");
  const [briefPrompt, setBriefPrompt] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setK(data)).catch((e) => setErr(e?.response?.data?.detail || "Failed"));
  }, []);

  // Allow MorningBrief to open the panel with a prefilled prompt — handled via custom event
  useEffect(() => {
    if (!briefPrompt) return;
    window.dispatchEvent(new CustomEvent("gr8agentic:ask", { detail: { prompt: briefPrompt } }));
    setBriefPrompt(null);
  }, [briefPrompt]);

  const cards = [
    { label: "Outstanding AR", value: k ? `${tenant?.currency || ""} ${k.outstanding_ar.toLocaleString()}` : "—", icon: Wallet, accent: "#D90429", testid: "kpi-ar" },
    { label: "Revenue (30d)", value: k ? `${tenant?.currency || ""} ${k.revenue_30d.toLocaleString()}` : "—", icon: TrendingUp, accent: "#0B7A75", testid: "kpi-revenue" },
    { label: "Sales orders", value: k?.sales_orders ?? "—", icon: ShoppingCart, accent: "#0033A0", testid: "kpi-orders" },
    { label: "Customers", value: k?.customers ?? "—", icon: Users, accent: "#0A0A0A", testid: "kpi-customers" },
    { label: "Items in catalogue", value: k?.items ?? "—", icon: Package, accent: "#0A0A0A", testid: "kpi-items" },
    { label: "Low stock items", value: k?.low_stock_items ?? "—", icon: AlertTriangle, accent: "#F5B700", testid: "kpi-lowstock" },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-12 max-w-[1400px]" data-testid="dashboard-page">
      <PageHeader title="Dashboard" subtitle={`Welcome to ${tenant?.name || "Gr8Boost ERP"} — live snapshot below.`} />

      {err && <div className="text-sm text-[#D90429] mb-6">{err}</div>}

      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-0 border border-[#0A0A0A] mt-6 sm:mt-8">
        {cards.map((c, i) => {
          const Icon = c.icon;
          const isLastCol = (i + 1) % 3 === 0;
          const isLastRow = i >= cards.length - (cards.length % 3 || 3);
          return (
            <div
              key={c.label}
              data-testid={c.testid}
              className={`p-4 sm:p-6 lg:p-7 bg-white ${!isLastCol ? "lg:border-r border-[#0A0A0A]" : ""} ${(i + 1) % 2 === 0 ? "md:border-r-0 lg:border-r" : "md:border-r border-[#0A0A0A]"} ${isLastCol ? "lg:border-r-0" : ""} ${!isLastRow ? "border-b border-[#0A0A0A]" : ""}`}
            >
              <div className="flex items-start justify-between mb-4 sm:mb-6">
                <div className="w-8 h-8 sm:w-10 sm:h-10 border border-[#0A0A0A] flex items-center justify-center">
                  <Icon size={16} style={{ color: c.accent }} />
                </div>
                <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-[#495057]">K0{i + 1}</span>
              </div>
              <div className="font-heading text-2xl sm:text-3xl font-bold tracking-tighter text-[#0A0A0A]">{c.value}</div>
              <div className="text-[10px] sm:text-xs uppercase tracking-[0.15em] text-[#495057] mt-1.5 sm:mt-2">{c.label}</div>
            </div>
          );
        })}
      </div>

      <MorningBrief onAskAgent={(p) => setBriefPrompt(p)} />

      <div className="mt-10 grid lg:grid-cols-2 gap-6">
        <Card title="Getting started" testid="getting-started">
          <ol className="space-y-3 text-sm">
            {[
              "Browse the seeded customers, items and warehouses",
              "Create a sales quotation → confirm → invoice → record payment",
              "Watch stock decrement & AR ledger post automatically",
              "Open Gr8Agentic AI (bottom of sidebar) and ask a data question",
            ].map((s, i) => (
              <li key={s} className="grid grid-cols-[auto_1fr] gap-3">
                <span className="font-mono text-xs text-[#FF4F00] tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                <span>{s}</span>
              </li>
            ))}
          </ol>
        </Card>
        <Card title="What Gr8Agentic AI can do" testid="agentic-help">
          <ul className="space-y-2 text-sm text-[#495057]">
            <li>• Natural-language queries against your live workspace data</li>
            <li>• Returns answers + structured tables when relevant</li>
            <li>• Read-only & workspace-scoped — your data never leaks across workspaces</li>
            <li>• Powered by Claude Sonnet 4.5 (Anthropic)</li>
          </ul>
        </Card>
      </div>
    </div>
  );
}

export function PageHeader({ title, subtitle, action }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 sm:gap-4 pb-4 sm:pb-6 border-b border-[#DEE2E6]">
      <div className="min-w-0">
        <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-[#FF4F00] font-mono">/ Gr8Boost ERP</div>
        <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold tracking-tighter mt-1" data-testid="page-title">{title}</h1>
        {subtitle && <p className="text-[#495057] mt-1 sm:mt-2 text-sm sm:text-base">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

function Card({ title, children, testid }) {
  return (
    <div className="border border-[#DEE2E6] bg-[#F8F9FA] p-6" data-testid={testid}>
      <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0A0A0A] mb-4">{title}</div>
      {children}
    </div>
  );
}
